import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import {
  buildChangeTrackingHeaders,
  buildChangeTrackingRequestConfig,
} from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components';
import { AddOrEditCommentPayload } from '@store/customers/customerDetails';
import { Comments } from '@typeDefinitions/comments';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { createError } from '@utils/errorUtils';
import { handlePopupToast, handleToast } from '@utils/utils';

import {
  ValidateAddressRequest,
  ValidateAddressResponse,
  VendorDetails,
  VendorDocument,
  VendorListData,
  VendorLoadHistory,
  VendorSearchResult,
  VendorServiceTypeList,
} from './types';

export const fetchVendorDetails = async (vendorId: number) => {
  try {
    const response: VendorDetails = await apiClient.get(
      `/Vendor/details/${vendorId}`,
    );
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to fetch vendor details');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchVendorsList = async (payload: unknown) => {
  try {
    const response: V4ApiResponse<{
      data: VendorListData[];
      totalCount: number;
    }> = await apiClient.post('/Vendor/list', payload);
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to fetch vendors list');
    handleToast(ToastType.Error, error.message);
  }
};

export const searchVendors = async (params: {
  name?: string;
  address?: string;
  serviceTypes?: number[];
  zip?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  state?: string;
}) => {
  try {
    const response: V4ApiResponse<VendorSearchResult[]> = await apiClient.get(
      '/Vendor/Search',
      {
        params: params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          if (params.name) {
            searchParams.append('name', params.name as string);
          }
          if (params.address) {
            searchParams.append('address', params.address as string);
          }
          if (params.serviceTypes && Array.isArray(params.serviceTypes)) {
            params.serviceTypes.forEach((serviceType: number) => {
              searchParams.append('serviceTypes', serviceType.toString());
            });
          }
          if (params.city) {
            searchParams.append('city', params.city as string);
          }
          if (params.state) {
            searchParams.append('state', params.state as string);
          }
          if (params.zip) {
            searchParams.append('zip', params.zip as string);
          }
          if (params.country) {
            searchParams.append('country', params.country as string);
          }
          if (params.latitude) {
            searchParams.append('latitude', String(params.latitude));
          }
          if (params.longitude) {
            searchParams.append('longitude', String(params.longitude));
          }
          return searchParams.toString();
        },
      },
    );
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to search vendors');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchVendorServicesList = async () => {
  try {
    const response: V4ApiResponse<VendorServiceTypeList[]> =
      await apiClient.get('/Vendor/servicetypes');
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to fetch vendor services list');
    handleToast(ToastType.Error, error.message);
  }
};

export const updateVendorDetails = async (
  vendorDetails: VendorDetails,
  changedSources: readonly ChangeTrackingSource[] = [],
) => {
  try {
    const response: V4ApiResponse<VendorDetails> = await apiClient.put(
      '/Vendor',
      vendorDetails,
      buildChangeTrackingRequestConfig(changedSources),
    );
    handlePopupToast(ToastType.Success, 'Vendor details saved successfully');
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to save vendor details');
    handleToast(ToastType.Error, error.message);
  }
};

export const saveNewVendorDetails = async (
  vendorDetails: VendorDetails,
  changedSources: readonly ChangeTrackingSource[] = [],
) => {
  try {
    const response: V4ApiResponse<VendorDetails> = await apiClient.post(
      '/Vendor',
      vendorDetails,
      buildChangeTrackingRequestConfig(changedSources),
    );
    handlePopupToast(ToastType.Success, 'Vendor details saved successfully');
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to save vendor details');
    handleToast(ToastType.Error, error.message);
  }
};

export const syncVendorWithIntacct = async (
  id: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<VendorDetails> = await apiClient.post(
      `/Vendor/AccountingSync/${id}`,
      {},
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    handleToast(ToastType.Success, 'Vendor Successfully Synced');
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to sync vendor with Intacct');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchVendorDocuments = async (vendorId: number) => {
  try {
    const response: V4ApiResponse<VendorDocument[]> = await apiClient.get(
      `/Vendor/getdocument/${vendorId}`,
    );
    return response.value;
  } catch (e) {
    const error = createError(e, 'Fetching vendor documents failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const uploadVendorDetailsDocument = async (
  vendorId: number,
  file: File,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const formData = new FormData();
    formData.append('Files', file);
    formData.append('DocumentEntityType', '4');
    formData.append('EntityId', vendorId.toString());
    const res: V4ApiResponse<{
      results: {
        documentId: number;
        name: string;
        url: string;
        taggedCategoryKeys: string[];
      }[];
    }> = await apiClient.post('/Vendor/uploaddocument', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(changeTrackingSource
          ? buildChangeTrackingHeaders([changeTrackingSource])
          : undefined),
      },
    });
    return res.value;
  } catch (e) {
    const error = createError(e, 'Uploading vendor document failed');
    throw error;
  }
};

export async function deleteVendorDetailsDocument(
  payload: { file: string },
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const res: V4ApiResponse<null> = await apiClient.delete(
      '/Vendor/deletedocument',
      {
        data: payload,
        ...(changeTrackingSource
          ? buildChangeTrackingRequestConfig([changeTrackingSource])
          : undefined),
      },
    );
    return res;
  } catch (e) {
    const error = createError(e, 'Deleting vendor document failed');
    handleToast(ToastType.Error, error.message);
  }
}

export const postVendorComment = async (
  payload: AddOrEditCommentPayload,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: V4ApiResponse<Comments> = await apiClient.post(
      '/Vendor/comment',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return res;
  } catch (e: unknown) {
    const error = createError(e, 'Adding vendor comment failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const deleteVendorComment = async (
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: V4ApiResponse<null> = await apiClient.delete(
      `/Vendor/comment/${commentId}`,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Deleting vendor comment failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const updateVendorComment = async (
  payload: AddOrEditCommentPayload,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: { isSuccess: boolean } = await apiClient.put(
      '/Vendor/comment',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Updating vendor comment failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchVendorLoadHistory = async (payload: unknown) => {
  try {
    const response: V4ApiResponse<{
      data: VendorLoadHistory[];
      totalCount: number;
    }> = await apiClient.post('/Vendor/loadhistory', payload);
    return response;
  } catch (e) {
    const error = createError(e, 'Unable to fetch vendor load history');
    handleToast(ToastType.Error, error.message);
  }
};

export async function validateAddress(
  payload: ValidateAddressRequest,
): Promise<ValidateAddressResponse> {
  try {
    const response: ValidateAddressResponse = await apiClient.post(
      'Location/ValidateAddress',
      payload,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to validate address');
    handleToast(ToastType.Error, error.message);
    throw error;
  }
}
